import React, { useState, useEffect } from 'react';
import {
  FiFileText, FiCalendar, FiActivity, FiBook, FiBarChart2,
  FiPlus, FiSearch, FiFilter, FiX, FiUsers
} from 'react-icons/fi';
import { useAuth } from '../../contexts/AuthContext';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../../firebase/config';
import MealPlanner from '../MealPlanner/MealPlanner';
import RecipeBuilder from '../RecipeBuilder/RecipeBuilder';
import FoodLogger from '../FoodLogger/FoodLogger';
import NutritionPlanWizard from '../Nutrition/NutritionPlanner/NutritionPlanWizard';
import './NutritionManagement.css';

const NutritionManagement = ({ clients }) => {
  const { currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState('plans');
  const [nutritionPlans, setNutritionPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all'); // all, active, completed
  const [showWizard, setShowWizard] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [showRecipeBuilder, setShowRecipeBuilder] = useState(false);
  const [showPlanTypeSelector, setShowPlanTypeSelector] = useState(false);
  const [showClientSelector, setShowClientSelector] = useState(false);

  const tabs = [
    { id: 'plans', label: 'Nutrition Plans', icon: FiFileText },
    { id: 'meals', label: 'Meal Plans', icon: FiCalendar },
    { id: 'logs', label: 'Client Logs', icon: FiActivity },
    { id: 'recipes', label: 'Recipes', icon: FiBook },
    { id: 'analytics', label: 'Analytics', icon: FiBarChart2 }
  ];

  useEffect(() => {
    if (activeTab === 'plans') {
      fetchNutritionPlans();
    }
  }, [currentUser, activeTab]);

  const fetchNutritionPlans = async () => {
    if (!currentUser) return;

    try {
      setLoading(true);
      const plansRef = collection(db, 'nutritionPlans');
      const plansQuery = query(
        plansRef,
        where('coachId', '==', currentUser.uid),
        orderBy('createdAt', 'desc')
      );

      const querySnapshot = await getDocs(plansQuery);
      const plans = [];

      querySnapshot.forEach((doc) => {
        plans.push({
          id: doc.id,
          ...doc.data()
        });
      });

      setNutritionPlans(plans);
    } catch (error) {
      console.error('Error fetching nutrition plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const filterPlans = () => {
    let filtered = nutritionPlans;

    if (filterStatus !== 'all') {
      filtered = filtered.filter(plan => plan.status === filterStatus);
    }

    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(plan => {
        // For templates, search by plan name
        if (isTemplate(plan)) {
          const planName = plan.name?.toLowerCase() || 'nutrition template';
          return planName.includes(query) || plan.goal?.toLowerCase().includes(query);
        }

        // For client plans, search by client name
        const clientName = clients.find(c => c.id === plan.clientId)?.name || '';
        return clientName.toLowerCase().includes(query) ||
               plan.goal?.toLowerCase().includes(query);
      });
    }

    return filtered;
  };

  const getClientName = (clientId) => {
    if (!clientId || clientId === 'template') {
      return null; // Return null for templates
    }
    const client = clients.find(c => c.id === clientId);
    return client ? client.name : 'Unknown Client';
  };

  const isTemplate = (plan) => {
    return !plan.clientId || plan.clientId === 'template' || plan.isTemplate;
  };

  const handleCreatePlan = () => {
    setShowPlanTypeSelector(true);
  };

  const handleChooseClient = () => {
    setShowPlanTypeSelector(false);
    setShowClientSelector(true);
  };

  const handleClientSelected = (client) => {
    setSelectedClient(client);
    setShowClientSelector(false);
    setShowWizard(true);
  };

  const handleCreateTemplate = () => {
    setSelectedClient(null);
    setShowPlanTypeSelector(false);
    setShowWizard(true);
  };

  const handleSavePlan = async (planData) => {
    // Plan is saved by the wizard
    await fetchNutritionPlans();
    setShowWizard(false);
    setSelectedClient(null);
  };

  const renderPlansTab = () => {
    const filteredPlans = filterPlans();

    return (
      <div className="plans-tab">
        <div className="tab-header">
          <div className="header-left">
            <h2>Nutrition Plans</h2>
            <p>{nutritionPlans.length} total plans</p>
          </div>
          <button className="create-plan-btn" onClick={handleCreatePlan}>
            <FiPlus />
            Create Nutrition Plan
          </button>
        </div>

        <div className="filters-bar">
          <div className="search-box">
            <FiSearch />
            <input
              type="text"
              placeholder="Search plans by client, template name, or goal..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <div className="filter-controls">
            <FiFilter />
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
              <option value="all">All Plans</option>
              <option value="active">Active</option>
              <option value="completed">Completed</option>
            </select>
          </div>
        </div>

        {loading ? (
          <div className="loading-state">
            <p>Loading nutrition plans...</p>
          </div>
        ) : filteredPlans.length === 0 ? (
          <div className="empty-state">
            <FiFileText size={48} />
            <h3>No Nutrition Plans Yet</h3>
            <p>Create your first nutrition plan for a client to get started.</p>
            <button className="create-first-btn" onClick={handleCreatePlan}>
              <FiPlus />
              Create First Plan
            </button>
          </div>
        ) : (
          <div className="plans-grid">
            {filteredPlans.map((plan) => (
              <div key={plan.id} className={`plan-card ${isTemplate(plan) ? 'template-card' : ''}`}>
                <div className="plan-header">
                  <div className="client-info">
                    {isTemplate(plan) ? (
                      <>
                        <h3>
                          <FiFileText style={{ marginRight: '0.5rem', color: 'var(--brand-orange)' }} />
                          {plan.name || 'Nutrition Template'}
                        </h3>
                        <span className="status-badge template">
                          Template
                        </span>
                      </>
                    ) : (
                      <>
                        <h3>{getClientName(plan.clientId)}</h3>
                        <span className={`status-badge ${plan.status}`}>
                          {plan.status}
                        </span>
                      </>
                    )}
                  </div>
                  <div className="plan-actions">
                    <button className="action-btn" title="Edit">
                      <FiActivity />
                    </button>
                  </div>
                </div>

                <div className="plan-details">
                  <div className="detail-row">
                    <span className="label">Goal:</span>
                    <span className="value">{plan.goal || 'Not set'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Daily Calories:</span>
                    <span className="value">
                      {plan.calculations?.targetCalories || 'N/A'} kcal
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Protein:</span>
                    <span className="value">
                      {plan.calculations?.macros?.protein?.grams || 'N/A'}g
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="label">Created:</span>
                    <span className="value">
                      {plan.createdAt?.toDate?.()?.toLocaleDateString() || 'N/A'}
                    </span>
                  </div>
                </div>

                <div className="plan-footer">
                  <button className="view-btn">View Details</button>
                  <button className="duplicate-btn">Duplicate</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Plan Type Selector Modal - Step 1 */}
        {showPlanTypeSelector && (
          <div className="modal-overlay" onClick={() => setShowPlanTypeSelector(false)}>
            <div className="modal-content plan-type-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Create Nutrition Plan</h3>
                <button className="modal-close" onClick={() => setShowPlanTypeSelector(false)}>
                  <FiX />
                </button>
              </div>
              <div className="plan-type-selector-content">
                <p className="selector-description">
                  Choose how you want to create your nutrition plan.
                </p>

                <div className="plan-type-options">
                  {/* Template Option */}
                  <div
                    className="plan-type-card template-card"
                    onClick={handleCreateTemplate}
                  >
                    <div className="plan-type-icon template-icon">
                      <FiFileText />
                    </div>
                    <div className="plan-type-info">
                      <h4>Create Template</h4>
                      <p>Reusable plan not linked to a specific client</p>
                    </div>
                  </div>

                  {/* Client Plan Option */}
                  <div
                    className="plan-type-card client-card"
                    onClick={handleChooseClient}
                  >
                    <div className="plan-type-icon client-icon">
                      <FiUsers />
                    </div>
                    <div className="plan-type-info">
                      <h4>For a Client</h4>
                      <p>Create a personalized plan for a specific client</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Client Selector Modal - Step 2 */}
        {showClientSelector && (
          <div className="modal-overlay" onClick={() => setShowClientSelector(false)}>
            <div className="modal-content" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h3>Select Client</h3>
                <button className="modal-close" onClick={() => setShowClientSelector(false)}>
                  <FiX />
                </button>
              </div>
              <div className="client-selector-content">
                <p className="selector-description">
                  Choose which client you want to create a nutrition plan for.
                </p>

                {/* Client List */}
                <div className="client-selector-list">
                  {clients && clients.length > 0 ? (
                    clients.map((client) => (
                      <div
                        key={client.id}
                        className="client-selector-item"
                        onClick={() => handleClientSelected(client)}
                      >
                        <div className="client-avatar">
                          {client.name ? client.name.charAt(0).toUpperCase() : 'C'}
                        </div>
                        <div className="client-info">
                          <div className="client-name">{client.name || 'Unnamed Client'}</div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="no-clients-message">
                      <p>No clients available yet.</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Nutrition Plan Wizard Modal */}
        {showWizard && (
          <div className="modal-overlay" onClick={() => setShowWizard(false)}>
            <div className="modal-content-large" onClick={(e) => e.stopPropagation()}>
              <button className="modal-close" onClick={() => setShowWizard(false)}>
                <FiX />
              </button>
              <NutritionPlanWizard
                clientId={selectedClient?.id}
                clientProfile={selectedClient}
                onClose={() => setShowWizard(false)}
                onSave={handleSavePlan}
              />
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderMealsTab = () => {
    return (
      <div className="meals-tab">
        <div className="tab-header">
          <h2>Meal Planning</h2>
          <p>Create and manage weekly meal plans for clients</p>
        </div>
        <MealPlanner />
      </div>
    );
  };

  const renderLogsTab = () => {
    return (
      <div className="logs-tab">
        <div className="tab-header">
          <h2>Client Food Logs</h2>
          <p>View and analyze client meal logs</p>
        </div>
        <div className="logs-content">
          <p className="coming-soon">Advanced log analytics coming soon...</p>
          <div className="logs-preview">
            <FoodLogger />
          </div>
        </div>
      </div>
    );
  };

  const renderRecipesTab = () => {
    return (
      <div className="recipes-tab">
        <div className="tab-header">
          <div className="header-left">
            <h2>Recipe Library</h2>
            <p>Create and manage recipes</p>
          </div>
          <button className="create-recipe-btn" onClick={() => setShowRecipeBuilder(true)}>
            <FiPlus />
            Create Recipe
          </button>
        </div>
        <RecipeBuilder />

        {showRecipeBuilder && (
          <div className="modal-overlay" onClick={() => setShowRecipeBuilder(false)}>
            <div className="modal-content-fullscreen" onClick={(e) => e.stopPropagation()}>
              <RecipeBuilder onClose={() => setShowRecipeBuilder(false)} />
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderAnalyticsTab = () => {
    return (
      <div className="analytics-tab">
        <div className="tab-header">
          <h2>Nutrition Analytics</h2>
          <p>Compliance trends and progress analysis</p>
        </div>
        <div className="analytics-content">
          <div className="analytics-placeholder">
            <FiBarChart2 size={64} />
            <h3>Analytics Dashboard</h3>
            <p>Advanced nutrition analytics coming soon...</p>
          </div>
        </div>
      </div>
    );
  };

  const renderActiveTab = () => {
    switch (activeTab) {
      case 'plans':
        return renderPlansTab();
      case 'meals':
        return renderMealsTab();
      case 'logs':
        return renderLogsTab();
      case 'recipes':
        return renderRecipesTab();
      case 'analytics':
        return renderAnalyticsTab();
      default:
        return renderPlansTab();
    }
  };

  return (
    <div className="nutrition-management">
      <div className="nutrition-tabs">
        {tabs.map((tab) => {
          const IconComponent = tab.icon;
          return (
            <button
              key={tab.id}
              className={`tab-button ${activeTab === tab.id ? 'active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              <IconComponent />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      <div className="tab-content">
        {renderActiveTab()}
      </div>
    </div>
  );
};

export default NutritionManagement;
